import React, { useCallback, useEffect, useRef, useState } from 'react';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import DialogForm from './DialogForm';
import { useWallet, useWalletAddressForMint } from '../utils/wallet';
import { PublicKey } from '@solana/web3.js';
import { abbreviateAddress, isExtension } from '../utils/utils';
import InputAdornment from '@material-ui/core/InputAdornment';
import { useCallAsync, useSendTransaction } from '../utils/notifications';
import { swapApiRequest, useSwapApiGet } from '../utils/swap/api';
import { showSwapAddress } from '../utils/config';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import DialogContentText from '@material-ui/core/DialogContentText';
import {
  ConnectToMetamaskButton,
  getErc20Balance,
  useEthAccount,
  withdrawEth,
} from '../utils/swap/eth';
import { useConnection, useIsProdNetwork } from '../utils/connection';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import QrcodeIcon from 'mdi-material-ui/Qrcode';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  TOKEN_PROGRAM_ID,
  WRAPPED_DOMI_MINT,
} from '../utils/tokens/instructions';
import { parseTokenAccountData } from '../utils/tokens/data';
import { Switch, Tooltip } from '@material-ui/core';
import { EthFeeEstimate } from './EthFeeEstimate';
import {
  resolveDomainName,
  resolveTwitterHandle,
  getNameKey,
} from '../utils/name-service';
import strings from '../localization';
import { createPortal } from 'react-dom';
import { Close } from '@material-ui/icons';
import { useQuery } from '@tanstack/react-query';

const WUSDC_MINT = new PublicKey(
  'BXXkv6z8ykpG1yuvUDPgh732wzVHB69RnB9YgSYh3itW',
);
const USDC_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v');

const WUSDT_MINT = new PublicKey(
  'BQcdHdAQW1hczDbBi9hiegXAR7A98Q9jx3X3iBBBDiq4',
);

const USDT_MINT = new PublicKey('Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB');
const DISABLED_ERC20_MINTS = new Set([
  'kinXdEcpDQeHPEuQnqmUgtYykqKGVFq6CeVX5iAHJq6',
  'ABE7D8RU1eHfCJWzHYZZeymeE8k9nPPXfqge2NQYyKoL',
]);

export default function SendDialog({ open, onClose, publicKey, balanceInfo }) {
  const isProdNetwork = useIsProdNetwork();
  const [tab, setTab] = useState('dpl');
  const onSubmitRef = useRef();

  let { data: swapCoinInfo } = useSwapApiGet(
    showSwapAddress && balanceInfo.mint && isProdNetwork
      ? `coins/sol/${balanceInfo.mint.toBase58()}`
      : null,
  );

  // SwapInfos to ignore.
  if (
    swapCoinInfo &&
    swapCoinInfo.erc20Contract === '0x2b2e04bf86978b45bb2edf54aca876973bdd43c0'
  ) {
    swapCoinInfo = null;
  }

  const ethAccount = useEthAccount();
  const { mint, tokenName, tokenSymbol } = balanceInfo;

  const getTabs = (mint) => {
    // if dwallet-private key not there, just show the DPL tab
    if (!localStorage.getItem('dwallet-private'))
      return [
        <Tab label={`DPL ${swapCoinInfo.ticker}`} key="dpl" value="dpl" />,
        <Tab label={`Wormhole`} key="wormhole" value="wormhole" />,
      ];

    if (mint?.equals(WUSDC_MINT)) {
      return [
        <Tab label="DPL WUSDC" key="dpl" value="dpl" />,
        <Tab label={`Wormhole`} key="wormhole" value="wormhole" />,
        <Tab label="DPL USDC" key="wusdcToDplUsdc" value="wusdcToDplUsdc" />,
        <Tab label="ERC20 USDC" key="swap" value="swap" />,
      ];
    } else if (mint?.equals(WUSDT_MINT)) {
      return [
        <Tab label="DPL WUSDT" key="dpl" value="dpl" />,
        <Tab label={`Wormhole`} key="wormhole" value="wormhole" />,
        <Tab label="DPL USDT" key="wusdtToDplUsdt" value="wusdtToDplUsdt" />,
        <Tab label="ERC20 USDT" key="swap" value="swap" />,
      ];
    } else if (
      localStorage.getItem('dwallet-private') &&
      mint?.equals(USDC_MINT)
    ) {
      return [
        <Tab label="DPL USDC" key="dpl" value="dpl" />,
        <Tab label={`Wormhole`} key="wormhole" value="wormhole" />,
        <Tab label="DPL WUSDC" key="usdcToDplWUsdc" value="usdcToDplWUsdc" />,
        <Tab label="ERC20 USDC" key="swap" value="swap" />,
      ];
    } else {
      const erc20Tab = (
        <Tab
          label={`${swapCoinInfo.erc20Contract ? 'ERC20' : 'Native'} ${
            swapCoinInfo.ticker
          }`}
          key="swap"
          value="swap"
        />
      );
      const tabs = [
        <Tab label={`DPL ${swapCoinInfo.ticker}`} key="dpl" value="dpl" />,
        <Tab label={`Wormhole`} key="wormhole" value="wormhole" />,
      ];
      if (
        !DISABLED_ERC20_MINTS.has(mint.toString()) ||
        localStorage.getItem('dwallet-private')
      ) {
        tabs.push(erc20Tab);
      }
      return tabs;
    }
  };

  return (
    <>
      <DialogForm
        open={open}
        onClose={onClose}
        onSubmit={() => onSubmitRef.current()}
        fullWidth
      >
        <DialogTitle>
          {strings.send} {tokenName ?? abbreviateAddress(mint)}
          {tokenSymbol ? ` (${tokenSymbol})` : null}
          {ethAccount && (
            <div>
              <Typography color="textSecondary" style={{ fontSize: '14px' }}>
                {strings.metamaskConnected}: {ethAccount}
              </Typography>
            </div>
          )}
        </DialogTitle>
        {swapCoinInfo ? (
          <Tabs
            value={tab}
            variant="fullWidth"
            onChange={(e, value) => setTab(value)}
            textColor="primary"
            indicatorColor="primary"
          >
            {getTabs(mint)}
          </Tabs>
        ) : (
          <Tabs
            value={tab}
            variant="fullWidth"
            onChange={(e, value) => setTab(value)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="DPL" key="dpl" value="dpl" />
            <Tab label={`Wormhole`} key="wormhole" value="wormhole" />
          </Tabs>
        )}
        {tab === 'dpl' ? (
          <SendDplDialog
            onClose={onClose}
            publicKey={publicKey}
            balanceInfo={balanceInfo}
            onSubmitRef={onSubmitRef}
          />
        ) : tab === 'wormhole' ? (
          <DialogContent>
            <DialogContentText>
              {strings.use1}{' '}
              <a
                href="https://www.portalbridge.com/#/transfer"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'white' }}
              >
                {strings.use2}
              </a>{' '}
              {strings.use3}
            </DialogContentText>
          </DialogContent>
        ) : tab === 'wusdcToDplUsdc' ? (
          <SendSwapDialog
            key={tab}
            onClose={onClose}
            publicKey={publicKey}
            balanceInfo={balanceInfo}
            swapCoinInfo={swapCoinInfo}
            onSubmitRef={onSubmitRef}
            wusdcToDplUsdc
          />
        ) : tab === 'wusdtToDplUsdt' ? (
          <SendSwapDialog
            key={tab}
            onClose={onClose}
            publicKey={publicKey}
            balanceInfo={balanceInfo}
            swapCoinInfo={swapCoinInfo}
            onSubmitRef={onSubmitRef}
            wusdtToDplUsdt
          />
        ) : tab === 'usdcToDplWUsdc' ? (
          <SendSwapDialog
            key={tab}
            onClose={onClose}
            publicKey={publicKey}
            balanceInfo={balanceInfo}
            swapCoinInfo={swapCoinInfo}
            onSubmitRef={onSubmitRef}
            usdcToDplWUsdc
          />
        ) : (
          <SendSwapDialog
            key={tab}
            onClose={onClose}
            publicKey={publicKey}
            balanceInfo={balanceInfo}
            swapCoinInfo={swapCoinInfo}
            ethAccount={ethAccount}
            onSubmitRef={onSubmitRef}
          />
        )}
      </DialogForm>
      {ethAccount &&
      (swapCoinInfo?.blockchain === 'eth' || swapCoinInfo?.erc20Contract) ? (
        <EthWithdrawalCompleter ethAccount={ethAccount} publicKey={publicKey} />
      ) : null}
    </>
  );
}

function SendDplDialog({ onClose, publicKey, balanceInfo, onSubmitRef }) {
  const defaultAddressHelperText =
    !balanceInfo.mint || balanceInfo.mint.equals(WRAPPED_DOMI_MINT)
      ? strings.enterDomichainAddress
      : strings.enterDPLAddress;
  const wallet = useWallet();
  const [sendTransaction, sending] = useSendTransaction();
  const [addressHelperText, setAddressHelperText] = useState(
    defaultAddressHelperText,
  );
  const [passValidation, setPassValidation] = useState();
  const [overrideDestinationCheck, setOverrideDestinationCheck] =
    useState(false);
  const [shouldShowOverride, setShouldShowOverride] = useState();
  let { fields, destinationAddress, transferAmountString, validAmount } =
    useForm(balanceInfo, addressHelperText, passValidation);
  const { decimals, mint } = balanceInfo;
  const mintString = mint && mint.toBase58();
  const [isDomainName, setIsDomainName] = useState(false);
  const [domainOwner, setDomainOwner] = useState();

  useEffect(() => {
    (async () => {
      if (destinationAddress.startsWith('@')) {
        const twitterOwner = await resolveTwitterHandle(
          wallet.connection,
          destinationAddress.slice(1),
        );
        if (!twitterOwner) {
          setAddressHelperText(`This Twitter handle is not registered`);
          setPassValidation(undefined);
          setShouldShowOverride(undefined);
          return;
        }
        setIsDomainName(true);
        setDomainOwner(twitterOwner);
      }
      if (destinationAddress.endsWith('.domi')) {
        const name = destinationAddress.slice(0, -4);
        const hasSub = name.split('.').length === 2;

        let domainOwner;

        if (hasSub) {
          const sub = name.split('.')[0];
          const parentKey = await getNameKey(name.split('.')[1]);
          domainOwner = await resolveDomainName(
            wallet.connection,
            sub,
            parentKey,
          );
        } else {
          domainOwner = await resolveDomainName(wallet.connection, name);
        }

        if (!domainOwner) {
          setAddressHelperText(
            `This ${hasSub ? 'subdomain' : 'domain'} name is not registered`,
          );
          setPassValidation(undefined);
          setShouldShowOverride(undefined);
          return;
        }
        setIsDomainName(true);
        setDomainOwner(domainOwner);
      }
      if (!destinationAddress) {
        setAddressHelperText(defaultAddressHelperText);
        setPassValidation(undefined);
        setShouldShowOverride(undefined);
        return;
      }
      try {
        const destinationAccountInfo = await wallet.connection.getAccountInfo(
          new PublicKey(isDomainName ? domainOwner : destinationAddress),
        );
        setShouldShowOverride(false);

        if (destinationAccountInfo.owner.equals(TOKEN_PROGRAM_ID)) {
          const accountInfo = parseTokenAccountData(
            destinationAccountInfo.data,
          );
          if (accountInfo.mint.toBase58() === mintString) {
            setPassValidation(true);
            setAddressHelperText('Address is a valid DPL token address');
          } else {
            setPassValidation(false);
            setAddressHelperText('Destination address mint does not match');
          }
        } else {
          setPassValidation(true);
          setAddressHelperText(
            `Destination is a Domichain address: ${
              isDomainName ? domainOwner : destinationAddress
            }`,
          );
        }
      } catch (e) {
        console.log(`Received error validating address ${e}`);
        setAddressHelperText(defaultAddressHelperText);
        setShouldShowOverride(true);
        setPassValidation(undefined);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationAddress, wallet, mintString, isDomainName, domainOwner]);
  useEffect(() => {
    return () => {
      setOverrideDestinationCheck(false);
    };
  }, [setOverrideDestinationCheck]);
  async function makeTransaction() {
    let amount = Math.round(parseFloat(transferAmountString) * 10 ** decimals);
    if (!amount || amount <= 0) {
      throw new Error('Invalid amount');
    }
    return wallet.transferToken(
      publicKey,
      new PublicKey(isDomainName ? domainOwner : destinationAddress),
      amount,
      balanceInfo.mint,
      decimals,
      null,
      mint || overrideDestinationCheck,
    );
  }

  const disabled = shouldShowOverride
    ? !overrideDestinationCheck || sending || !validAmount
    : sending || !validAmount;

  async function onSubmit() {
    return sendTransaction(makeTransaction(), { onSuccess: onClose });
  }
  onSubmitRef.current = onSubmit;
  return (
    <>
      <DialogContent>{fields}</DialogContent>
      <DialogActions
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {shouldShowOverride && (
          <div
            style={{
              'align-items': 'center',
              display: 'flex',
              'text-align': 'left',
            }}
          >
            <b>{strings.addressHasNoFunds}</b>
            <Switch
              checked={overrideDestinationCheck}
              onChange={(e) => setOverrideDestinationCheck(e.target.checked)}
              color="primary"
            />
          </div>
        )}
        <div>
          <Button onClick={onClose}>{strings.cancel}</Button>
          <Button type="submit" color="primary" disabled={disabled}>
            {strings.send}
          </Button>
        </div>
      </DialogActions>
    </>
  );
}

function SendSwapDialog({
  onClose,
  publicKey,
  balanceInfo,
  swapCoinInfo,
  ethAccount,
  wusdcToDplUsdc = false,
  wusdtToDplUsdt = false,
  usdcToDplWUsdc = false,
  onSubmitRef,
}) {
  const wallet = useWallet();
  const [sendTransaction, sending] = useSendTransaction();
  const [signature, setSignature] = useState(null);
  const {
    fields,
    destinationAddress,
    transferAmountString,
    setDestinationAddress,
    validAmount,
  } = useForm(balanceInfo);

  const { tokenName, decimals, mint } = balanceInfo;
  const blockchain =
    wusdcToDplUsdc || wusdtToDplUsdt || usdcToDplWUsdc
      ? 'domi'
      : swapCoinInfo.blockchain === 'domi'
      ? 'eth'
      : swapCoinInfo.blockchain;

  const needMetamask = blockchain === 'eth';

  const { data: ethBalance } = useQuery({
    queryKey: ['ethBalance'],
    queryFn: () => getErc20Balance(ethAccount),
    refetchInterval: 2000,
  });
  const ethFeeData = useSwapApiGet(
    `fees/eth/${ethAccount}` +
      (swapCoinInfo.erc20Contract ? '/' + swapCoinInfo.erc20Contract : ''),
    { enabled: blockchain === 'eth', refreshInterval: 2000 },
  );
  const ethFeeEstimate = ethFeeData.data;
  const insufficientEthBalance =
    typeof ethBalance === 'number' &&
    typeof ethFeeEstimate === 'number' &&
    ethBalance < ethFeeEstimate;

  useEffect(() => {
    if (blockchain === 'eth' && ethAccount) {
      setDestinationAddress(ethAccount);
    }
  }, [blockchain, ethAccount, setDestinationAddress]);

  let dplUsdcWalletAddress = useWalletAddressForMint(
    wusdcToDplUsdc ? USDC_MINT : null,
  );
  let dplUsdtWalletAddress = useWalletAddressForMint(
    wusdtToDplUsdt ? USDT_MINT : null,
  );
  let dplWUsdcWalletAddress = useWalletAddressForMint(
    usdcToDplWUsdc ? WUSDC_MINT : null,
  );
  useEffect(() => {
    if (wusdcToDplUsdc && dplUsdcWalletAddress) {
      setDestinationAddress(dplUsdcWalletAddress);
    } else if (wusdtToDplUsdt && dplUsdtWalletAddress) {
      setDestinationAddress(dplUsdtWalletAddress);
    } else if (usdcToDplWUsdc && dplWUsdcWalletAddress) {
      setDestinationAddress(dplWUsdcWalletAddress);
    }
  }, [
    setDestinationAddress,
    wusdcToDplUsdc,
    dplUsdcWalletAddress,
    wusdtToDplUsdt,
    dplUsdtWalletAddress,
    usdcToDplWUsdc,
    dplWUsdcWalletAddress,
  ]);

  async function makeTransaction() {
    let amount = Math.round(parseFloat(transferAmountString) * 10 ** decimals);
    if (!amount || amount <= 0) {
      throw new Error('Invalid amount');
    }
    const params = {
      blockchain,
      address: destinationAddress,
      size: amount / 10 ** decimals,
    };
    if (blockchain === 'domi') {
      params.coin = swapCoinInfo.dplMint;
    } else if (blockchain === 'eth') {
      params.coin = swapCoinInfo.erc20Contract;
    }
    if (mint?.equals(WUSDC_MINT)) {
      params.wusdcToUsdc = true;
    } else if (mint?.equals(USDC_MINT)) {
      if (usdcToDplWUsdc) {
        params.usdcToWUsdc = true;
        params.coin = WUSDC_MINT.toString();
      }
    } else if (mint?.equals(WUSDT_MINT)) {
      params.wusdtToUsdt = true;
    }
    const swapInfo = await swapApiRequest('POST', 'swap_to', params);
    if (swapInfo.blockchain !== 'domi') {
      throw new Error('Unexpected blockchain');
    }
    return wallet.transferToken(
      publicKey,
      new PublicKey(swapInfo.address),
      amount,
      balanceInfo.mint,
      decimals,
      swapInfo.memo,
    );
  }

  async function onSubmit() {
    return sendTransaction(makeTransaction(), { onSuccess: setSignature });
  }
  onSubmitRef.current = onSubmit;

  if (signature) {
    return (
      <SendSwapProgress
        key={signature}
        publicKey={publicKey}
        signature={signature}
        blockchain={blockchain}
        onClose={onClose}
      />
    );
  }
  const bitcoinDisable =
    blockchain === 'btc' ? parseFloat(transferAmountString) < 0.001 : false;
  let sendButton = (
    <Button
      type="submit"
      color="primary"
      disabled={
        sending ||
        (needMetamask && !ethAccount) ||
        !validAmount ||
        insufficientEthBalance ||
        bitcoinDisable
      }
    >
      Send
    </Button>
  );

  if (insufficientEthBalance) {
    sendButton = (
      <Tooltip
        title="Insufficient ETH for withdrawal transaction fee"
        placement="top"
      >
        <span>{sendButton}</span>
      </Tooltip>
    );
  }

  return (
    <>
      <DialogContent style={{ paddingTop: 16 }}>
        <DialogContentText>
          DPL {tokenName} can be converted to{' '}
          {blockchain === 'eth' && swapCoinInfo.erc20Contract
            ? 'ERC20'
            : blockchain === 'domi' && swapCoinInfo.dplMint
            ? 'DPL'
            : 'native'}{' '}
          {swapCoinInfo.ticker}
          {needMetamask ? ' via MetaMask' : null}.
        </DialogContentText>
        {blockchain === 'eth' && (
          <DialogContentText>
            Estimated withdrawal transaction fee:
            <EthFeeEstimate
              ethFeeData={ethFeeData}
              insufficientEthBalance={insufficientEthBalance}
            />
          </DialogContentText>
        )}
        {needMetamask && !ethAccount ? <ConnectToMetamaskButton /> : fields}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.cancel}</Button>
        {sendButton}
      </DialogActions>
    </>
  );
}

function SendSwapProgress({ publicKey, signature, onClose, blockchain }) {
  const connection = useConnection();
  const { data: swaps } = useSwapApiGet(
    `swaps_from/domi/${publicKey.toBase58()}`,
    {
      refreshInterval: 1000,
    },
  );
  const { data: confirms } = useQuery({
    queryKey: ['getSignatureStatus', signature],
    queryFn: async () => {
      const { value } = await connection.getSignatureStatus(signature);
      return value?.confirmations;
    },
    refreshInterval: 2000,
  });

  let step = 1;
  let ethTxid = null;
  for (let swap of swaps || []) {
    const { deposit, withdrawal } = swap;
    if (deposit.txid === signature) {
      if (withdrawal.txid?.startsWith('0x')) {
        step = 3;
        ethTxid = withdrawal.txid;
      } else if (withdrawal.txid && blockchain !== 'eth') {
        step = 3;
      } else {
        step = 2;
      }
    }
  }

  return (
    <>
      <DialogContent>
        <Stepper activeStep={step}>
          <Step>
            <StepLabel>{strings.sendRequest}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{strings.waitingForConfirmation}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{strings.withdrawFunds}</StepLabel>
          </Step>
        </Stepper>
        {ethTxid ? (
          <Typography variant="body2" align="center">
            <Link
              href={`https://etherscan.io/tx/${ethTxid}`}
              target="_blank"
              rel="noopener"
            >
              View on Etherscan
            </Link>
          </Typography>
        ) : step < 3 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div style={{ marginRight: 16 }}>
              <CircularProgress />
            </div>
            {confirms ? (
              <Typography>{confirms} / 35 Confirmations</Typography>
            ) : (
              <Typography>{strings.transactionPending}</Typography>
            )}
          </div>
        ) : null}
        {!ethTxid && blockchain === 'eth' ? (
          <DialogContentText style={{ marginTop: 16, marginBottom: 0 }}>
            Please keep this window open. You will need to approve the request
            on MetaMask to complete the transaction.
          </DialogContentText>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.close}</Button>
      </DialogActions>
    </>
  );
}

function useForm(
  balanceInfo,
  addressHelperText,
  passAddressValidation,
  overrideValidation,
) {
  const [scannerStatus, setScannerStatus] = useState();
  const [scannerIsVisible, setScannerIsVisible] = useState(false);

  const [destinationAddress, setDestinationAddress] = useState('');
  const [transferAmountString, setTransferAmountString] = useState('');
  const { amount: balanceAmount, decimals, tokenSymbol } = balanceInfo;

  const parsedAmount = parseFloat(transferAmountString) * 10 ** decimals;
  const validAmount = parsedAmount > 0 && parsedAmount <= balanceAmount;

  const presentQrScanner = useCallback(() => {
    window.QRScanner.prepare((_, status) => {
      setScannerStatus(status);
      setScannerIsVisible(true);
    });
  }, []);

  useEffect(() => {
    if (scannerIsVisible) {
      if (scannerStatus?.denied) {
        window.QRScanner.openSettings();
        return;
      }

      if (!scannerStatus?.authorized) {
        return;
      }

      const handleScan = (err, text) => {
        if (err) {
          setScannerIsVisible(false);
          return;
        }

        if (text && PublicKey.isOnCurve(text)) {
          setDestinationAddress(text);
          setScannerIsVisible(false);
        }
      };

      const dialogElements = document.querySelectorAll('[role="presentation"]');
      const rootElement = document.getElementById('root');

      window.QRScanner.scan(handleScan);
      window.QRScanner.show(() => {
        dialogElements.forEach((el) => (el.style.display = 'none'));
        rootElement.style.display = 'none';
      });

      return () => {
        window.QRScanner.cancelScan();
        window.QRScanner.hide(() => {
          dialogElements.forEach((el) => (el.style.display = ''));
          rootElement.style.display = '';
        });
      };
    }
  }, [scannerStatus, scannerIsVisible]);

  const fields = (
    <>
      <TextField
        label={strings.recipientAddress}
        fullWidth
        variant="outlined"
        margin="normal"
        value={destinationAddress}
        onChange={(e) => setDestinationAddress(e.target.value.trim())}
        helperText={addressHelperText}
        id={
          !passAddressValidation && passAddressValidation !== undefined
            ? 'outlined-error-helper-text'
            : undefined
        }
        error={!passAddressValidation && passAddressValidation !== undefined}
        InputProps={
          !isExtension && {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton color="inherit" onClick={presentQrScanner}>
                  <QrcodeIcon />
                </IconButton>
              </InputAdornment>
            ),
          }
        }
      />
      <TextField
        label={strings.amount}
        fullWidth
        variant="outlined"
        margin="normal"
        type="number"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Button
                onClick={() =>
                  setTransferAmountString(
                    balanceAmountToUserAmount(balanceAmount, decimals),
                  )
                }
              >
                {strings.max}
              </Button>
              <Typography color="primary">
                {tokenSymbol ? tokenSymbol : null}
              </Typography>
            </InputAdornment>
          ),
          inputProps: {
            step: Math.pow(10, -decimals),
          },
        }}
        value={transferAmountString}
        onChange={(e) => setTransferAmountString(e.target.value.trim())}
        helperText={
          <span
            onClick={() =>
              setTransferAmountString(
                balanceAmountToUserAmount(balanceAmount, decimals),
              )
            }
          >
            {strings.max}: {balanceAmountToUserAmount(balanceAmount, decimals)}
          </span>
        }
      />
      {scannerIsVisible &&
        createPortal(
          <div
            style={{
              margin: 8,
              paddingTop: 'env(safe-area-inset-top)',
            }}
          >
            <IconButton
              color="inherit"
              onClick={() => setScannerIsVisible(false)}
            >
              <Close />
            </IconButton>
          </div>,
          document.body,
        )}
    </>
  );

  return {
    fields,
    destinationAddress,
    transferAmountString,
    setDestinationAddress,
    validAmount,
  };
}

function balanceAmountToUserAmount(balanceAmount, decimals) {
  return (balanceAmount / Math.pow(10, decimals)).toFixed(decimals) * 1;
}

function EthWithdrawalCompleter({ ethAccount, publicKey }) {
  const { data: swaps } = useSwapApiGet(
    `swaps_from/domi/${publicKey.toBase58()}`,
    {
      refreshInterval: 10000,
    },
  );
  if (!swaps) {
    return null;
  }
  return swaps.map((swap) => (
    <EthWithdrawalCompleterItem
      key={swap.deposit.txid}
      ethAccount={ethAccount}
      swap={swap}
    />
  ));
}

function EthWithdrawalCompleterItem({ ethAccount, swap }) {
  const callAsync = useCallAsync();
  const { withdrawal } = swap;
  useEffect(() => {
    if (
      withdrawal.status === 'sent' &&
      withdrawal.blockchain === 'eth' &&
      withdrawal.txid &&
      !withdrawal.txid.startsWith('0x') &&
      withdrawal.txData
    ) {
      withdrawEth(ethAccount, withdrawal, callAsync);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withdrawal.txid, withdrawal.status]);
  return null;
}
